import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Stethoscope } from 'lucide-react';
import { authService } from '@/services/auth.service';
import { cartService } from '@/services/cart.service';
import { useAuthStore } from '@/store/authStore';
import { useCartStore } from '@/store/cartStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { generateSessionId } from '@/utils';
import toast from '@/hooks/useToast';

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useAuthStore();
  const { setCart } = useCartStore();
  const from = (location.state as { from?: string })?.from || '/';

  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authService.login(form.email, form.password);
      const { user, accessToken, refreshToken } = res.data.data;
      setAuth(user, accessToken, refreshToken);

      // Merge guest cart
      const sessionId = generateSessionId();
      try {
        await cartService.mergeCart(sessionId);
        const cartRes = await cartService.getCart();
        setCart(cartRes.data.data);
      } catch { /* ignore cart merge errors */ }

      toast.success(`Welcome back, ${user.name.split(' ')[0]}!`);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Login failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-600 to-primary-800 p-12 flex-col justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <Stethoscope className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="font-heading font-bold text-white text-xl">Milan Medical</p>
            <p className="text-primary-200 text-xs">Pharmacy & Health Store</p>
          </div>
        </Link>
        <div className="text-white">
          <h2 className="font-heading font-bold text-4xl mb-4">Your Health Journey Starts Here</h2>
          <p className="text-primary-100 text-lg leading-relaxed">Access medicines, earn loyalty points, and get expert pharmacist support — all in one place.</p>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {['30+ Products', '100% Genuine', 'Expert Support'].map(t => (
              <div key={t} className="bg-white/10 rounded-xl p-3 text-center text-sm font-medium">{t}</div>
            ))}
          </div>
        </div>
        <p className="text-primary-300 text-sm">© {new Date().getFullYear()} Milan Medical, Nadiad, Gujarat</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Stethoscope className="h-8 w-8 text-primary-500" />
            <span className="font-heading font-bold text-2xl text-primary-600">Milan Medical</span>
          </div>
          <h1 className="font-heading font-bold text-2xl text-gray-800 mb-1">Welcome back</h1>
          <p className="text-gray-500 text-sm mb-8">Sign in to your account</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>
            )}
            <Input
              label="Email Address"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              required
            />
            <div className="relative">
              <Input
                label="Password"
                type={showPass ? 'text' : 'password'}
                placeholder="••••••••"
                value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                required
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute right-3 bottom-3 text-gray-400 hover:text-gray-600"
              >
                {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-primary-600 hover:text-primary-700 font-medium">Forgot password?</Link>
            </div>
            <Button type="submit" className="w-full" loading={loading}>Sign In</Button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary-600 font-semibold hover:text-primary-700">Create Account</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
